import React from "react";
import Icon from "../../../components/AppIcon";

const ComplianceSidebar = ({
  totalTronc = 0,
  totalDistributed = 0,
  remainingBalance = 0,
  employeeCount = 0,
  hasActiveRule = false,
  ruleName = null,
}) => {
  const isFullyDistributed = Math.abs(remainingBalance) < 0.01;
  const distributionPercentage =
    totalTronc > 0
      ? Math.min((totalDistributed / totalTronc) * 100, 100)
      : 0;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  // Compliance checks
  const complianceItems = [
    {
      title: "Active Rule",
      status: hasActiveRule ? "compliant" : "warning",
      statusText: hasActiveRule ? "Active" : "Missing",
      description: hasActiveRule ? ruleName : "No active distribution rule",
    },
    {
      title: "Distribution Balance",
      status: isFullyDistributed ? "compliant" : "warning",
      statusText: isFullyDistributed ? "Balanced" : "Incomplete",
      description:
        isFullyDistributed
          ? "All TRONC allocated"
          : `${formatCurrency(Math.abs(remainingBalance))} ${
              remainingBalance > 0 ? "remaining" : "over"
            }`,
    },
    {
      title: "Eligible Employees",
      status: employeeCount > 0 ? "compliant" : "warning",
      statusText: employeeCount > 0 ? `${employeeCount}` : "None",
      description:
        employeeCount > 0
          ? "Ready for distribution"
          : "No eligible employees",
    },
    {
      title: "HMRC Compliance",
      status: "compliant",
      statusText: "Ready",
      description: "TRONC separate from wages",
    },
    {
      title: "Audit Trail",
      status: "compliant",
      statusText: "Active",
      description: "All actions logged",
    },
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
      <div className="flex items-center gap-2 mb-4">
        <Icon
          name={isFullyDistributed && hasActiveRule ? "ShieldCheck" : "Shield"}
          size={20}
          className={
            isFullyDistributed && hasActiveRule
              ? "text-success"
              : "text-warning"
          }
        />
        <h3 className="font-semibold text-sm">Compliance Status</h3>
      </div>

      <div className="space-y-4">
        {/* Distribution Progress */}
        <div>
          <div className="flex justify-between text-xs mb-2">
            <span className="text-muted-foreground">Allocation Progress</span>
            <span className="font-semibold">
              {distributionPercentage.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                isFullyDistributed
                  ? "bg-success"
                  : distributionPercentage > 0
                  ? "bg-warning"
                  : "bg-muted-foreground/30"
              }`}
              style={{ width: `${Math.min(distributionPercentage, 100)}%` }}
            />
          </div>
        </div>

        {/* Amount Summary */}
        <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Total TRONC</span>
            <span className="font-semibold font-data">
              {formatCurrency(totalTronc)}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Allocated</span>
            <span className="font-semibold font-data text-success">
              {formatCurrency(totalDistributed)}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Remaining</span>
            <span
              className={`font-semibold font-data ${
                isFullyDistributed ? "text-success" : "text-warning"
              }`}
            >
              {formatCurrency(Math.abs(remainingBalance))}
              {remainingBalance < 0 && (
                <span className="text-error"> (over)</span>
              )}
            </span>
          </div>
        </div>

        {/* Status Indicator */}
        <div
          className={`p-3 rounded-lg ${
            isFullyDistributed && hasActiveRule
              ? "bg-success/10 border border-success/20"
              : "bg-warning/10 border border-warning/20"
          }`}
        >
          <div className="flex items-start gap-2">
            <Icon
              name={
                isFullyDistributed && hasActiveRule
                  ? "CheckCircle2"
                  : "AlertTriangle"
              }
              size={16}
              className={`flex-shrink-0 mt-0.5 ${
                isFullyDistributed && hasActiveRule
                  ? "text-success"
                  : "text-warning"
              }`}
            />
            <div>
              <p className="text-xs font-semibold mb-1">
                {isFullyDistributed && hasActiveRule
                  ? "Ready to Approve"
                  : "Action Required"}
              </p>
              <p className="text-xs text-muted-foreground">
                {isFullyDistributed && hasActiveRule
                  ? "All checks passed. You can approve this distribution."
                  : !hasActiveRule
                  ? "Activate a distribution rule to proceed."
                  : "Complete allocation to enable approval."}
              </p>
            </div>
          </div>
        </div>

        {/* Compliance Checklist */}
        <div className="pt-3 border-t border-border space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Compliance Checklist
          </p>
          {complianceItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <Icon
                name={
                  item.status === "compliant"
                    ? "CheckCircle2"
                    : "AlertCircle"
                }
                size={14}
                className={
                  item.status === "compliant"
                    ? "text-success"
                    : "text-warning"
                }
              />
              <span className="flex-1 text-muted-foreground">{item.title}</span>
              <span
                className={`font-medium ${
                  item.status === "compliant"
                    ? "text-success"
                    : "text-warning"
                }`}
              >
                {item.statusText}
              </span>
            </div>
          ))}
        </div>

        {/* Quick Info */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-start gap-2 text-xs">
            <Icon
              name="Info"
              size={14}
              className="text-muted-foreground mt-0.5 flex-shrink-0"
            />
            <p className="text-muted-foreground">
              TRONC distributions are logged for HMRC compliance. All allocations
              create audit records.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceSidebar;
